import React from 'react'
import createCache from '@emotion/cache'
import { set } from 'lodash'
import { Provider as JotaiProvider } from 'jotai'
import { DragDropContext, OnDragEndResponder } from 'react-beautiful-dnd'
import { Router } from 'react-router-dom'
import { CompatRouter } from 'react-router-dom-v5-compat'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import { createGenerateClassName, StylesProvider as MuiStylesProvider } from '@mui/styles'
import { CacheProvider as EmotionCacheProvider } from '@emotion/react'
import ViewRouter from './ViewRouter'
import { AppDialog, AppSnackbar, FeatureDialog } from '@edge/spark/feature-dialog'
import history from './utils/history'
import { muiThemeBase, mui5Breakpoints, mui4Breakpoints } from '@edge/spark/ui-mui'
import { useReorderUserDashboardsMutation } from '@edge/spark/io-dashboards'
import { CognitoAuthProvider } from '@edge/spark/io-aws'
import ApolloClient from './components/ApolloClient'
import { BugsnagErrorBoundary } from './spark_libs_deprecated/feature-bugsnag'
import googleAnalyticsInitialize from './utils/googleAnalyticsInitialize'
import hotjarInitialize from './utils/hotjarInitialize'
import { useDynamicTitle } from '@edge/spark/utils'
import { useDarkMode, useMui5Breakpoints, useSetFirstAsActiveAuction } from '@edge/spark/io-roles'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider as MuiLocalizationProvider } from '@mui/x-date-pickers'
import useLDContext from './utils/useLDContext'

import 'react-grid-layout/css/styles.css'
import './index.css'

googleAnalyticsInitialize()
hotjarInitialize()

const onDragStart = (): void => {
    // Add a little vibration if the browser supports it.
    if (window.navigator.vibrate) {
        window.navigator.vibrate(100)
    }
}

const emotionCache = createCache({
    key: 'emot',
})

const mui5prefix = createGenerateClassName({ productionPrefix: 'muijss' })

const App: React.FC = () => {
    useDynamicTitle('EDGE Spark')
    useSetFirstAsActiveAuction()
    const darkMode = useDarkMode()
    const mui5Brk = useMui5Breakpoints()

    const muiTheme = React.useMemo(() => {
        const config = muiThemeBase
        const withDarkMode = set(config, 'palette.mode', darkMode ? 'dark' : 'light')
        const withBreakpoints = set(withDarkMode, 'breakpoints', mui5Brk ? mui5Breakpoints : mui4Breakpoints)
        return createTheme(withBreakpoints)
    }, [darkMode, mui5Brk])

    const [reorderDashboards] = useReorderUserDashboardsMutation()

    const onDragEnd: OnDragEndResponder = async (result) => {
        if (result.destination?.droppableId === 'droppable-dashboards-list') {
            await reorderDashboards({
                variables: {
                    id: result.draggableId,
                    index: result.destination.index,
                },
            })
        }
    }

    // This is a custom hook that will update the LaunchDarkly context with the current user
    // has to be called somewhere in the app
    useLDContext()

    return (
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <JotaiProvider>
                <EmotionCacheProvider value={emotionCache}>
                    <MuiStylesProvider generateClassName={mui5prefix} injectFirst>
                        <MuiThemeProvider theme={muiTheme}>
                            <MuiLocalizationProvider dateAdapter={AdapterDateFns}>
                                <CognitoAuthProvider>
                                    <AppSnackbar />
                                    <AppDialog />
                                    <FeatureDialog />
                                    <ViewRouter />
                                </CognitoAuthProvider>
                            </MuiLocalizationProvider>
                        </MuiThemeProvider>
                    </MuiStylesProvider>
                </EmotionCacheProvider>
            </JotaiProvider>
        </DragDropContext>
    )
}

export const NxEntry: React.FC = () => {
    return (
        <BugsnagErrorBoundary>
            <ApolloClient>
                <App />
            </ApolloClient>
        </BugsnagErrorBoundary>
    )
}

const AppWithRouter: React.FC = () => {
    return (
        <Router history={history}>
            <CompatRouter>
                <App />
            </CompatRouter>
        </Router>
    )
}

export default AppWithRouter
